import { useEffect, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { useResetPasswordMutation } from "@/api/auth";

export function ResetPasswordPage() {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const router = useRouter();
  const mutation = useResetPasswordMutation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) setToken(t);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    mutation.mutate(
      { token, password },
      { onSuccess: () => setDone(true) },
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <div className="w-full max-w-md p-6">
          <div className="rounded-2xl border border-line bg-surface-2 p-6 text-center">
            <p className="text-sm text-danger">
              Missing reset token. Open the link from your password reset
              email.
            </p>
            <Link
              to="/forgot-password"
              className="mt-4 inline-block text-xs text-ink-2 hover:text-ink"
            >
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md p-6">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-ink">
            MotionForge
          </h1>
          <p className="mt-2 text-sm text-ink-3">
            Choose a new password
          </p>
        </div>

        <div className="rounded-2xl border border-line bg-surface-2 p-6">
          {done ? (
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-semibold text-ink">
                Password updated
              </h2>
              <p className="text-sm text-ink-2">
                You can now sign in with your new password.
              </p>
              <button
                onClick={() => router.navigate({ to: "/login" })}
                className="w-full rounded-lg bg-accent py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
              >
                Go to sign in
              </button>
            </div>
          ) : (
            <>
              <h2 className="mb-6 text-lg font-semibold text-ink">
                Reset password
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink-2">
                    New password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-ink-2">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                  />
                </div>

                {mismatch && (
                  <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                    Passwords don't match.
                  </div>
                )}

                {mutation.error && (
                  <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                    {mutation.error.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="w-full rounded-lg bg-accent py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
                >
                  {mutation.isPending ? "Saving..." : "Set new password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
